import React, { useReducer, Fragment, useState, useRef, useEffect } from 'react';
import { useThemeUI, jsx, merge } from 'theme-ui';
import { __ThemeUIInternalBaseThemeProvider } from '@theme-ui/core';
import { ColorPalette } from '@theme-ui/style-guide';
import { CustomPicker, GithubPicker } from 'react-color';
import { Saturation, Hue, EditableInput } from 'react-color/lib/components/common';
import { usePopoverState, PopoverDisclosure, Popover } from 'reakit/Popover';
import { Field, Label as Label$1, Input as Input$1, Checkbox } from '@theme-ui/components';
import tinycolor from 'tinycolor2';

const reducer = (state, next) => merge(state, next);

const EditorProvider = ({
  children,
  theme: propsTheme
}) => {
  const outer = useThemeUI();
  const [theme, setTheme] = useReducer(reducer, { ...(propsTheme || outer.theme)
  });
  const context = { ...outer,
    theme,
    setTheme
  };
  return jsx(__ThemeUIInternalBaseThemeProvider, {
    context,
    children
  });
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const Lens = () => jsx("div", {
  sx: {
    width: 16,
    height: 16,
    borderRadius: 9999,
    border: '1px solid white',
    transform: 'translate(-8px, -8px)'
  }
});

const Handle = () => jsx("div", {
  sx: {
    width: 12,
    height: 12,
    borderRadius: 9999,
    backgroundColor: 'white',
    boxShadow: '0 0 2px rgba(0,0,0,.25)',
    transform: 'translate(-6px, -2px)'
  }
});

/** placeholder is used because react-color does not pass
 * HTML attributes to the element
 */
const Input = props => jsx(EditableInput, _extends({}, props, {
  placeholder: props.name,
  style: {
    input: {
      width: '100%',
      fontSize: 12,
      fontFamily: 'Menlo, monospace',
      margin: 0,
      padding: 0,
      border: 0,
      outline: 'none'
    },
    label: {
      display: 'none'
    }
  }
}));

const Label = props => jsx("label", _extends({}, props, {
  sx: {
    display: 'block',
    width: '100%',
    fontSize: 10,
    input: {
      ':focus': {
        color: 'primary',
        bg: 'highlight'
      }
    }
  }
}));

const Picker = CustomPicker(({
  size = 256,
  ...props
}) => {
  return jsx("div", {
    sx: {
      display: 'grid',
      p: 2,
      gap: 2,
      width: size,
      borderRadius: 4,
      bg: 'white',
      boxShadow: '0 2px 8px 1px rgba(0,0,0,.125)'
    }
  }, jsx("div", {
    sx: {
      position: 'relative',
      width: '100%',
      height: 0,
      paddingBottom: '75%'
    }
  }, jsx(Saturation, _extends({}, props, {
    pointer: Lens
  }))), jsx("div", {
    sx: {
      position: 'relative',
      width: '100%',
      height: 8
    }
  }, jsx(Hue, _extends({}, props, {
    pointer: Handle
  }))), jsx("div", {
    sx: {
      display: 'flex'
    }
  }, jsx(Label, null, "Hex", jsx(Input, _extends({}, props, {
    value: props.hex,
    name: "hex",
    label: "hex",
    onChange: val => {
      props.onChange(val);
    }
  }))), jsx(Label, null, "Hue", jsx(Input, _extends({}, props, {
    value: Math.round(props.hsl.h),
    name: "hue",
    label: "h" // We need to cast to any because @types/react-color does not define types correctly
    ,
    onChange: ({
      h
    }) => {
      props.onChange({ ...props.hsl,
        h
      });
    }
  }))), jsx(Label, null, "Saturation", jsx(Input, _extends({}, props, {
    value: Math.round(props.hsl.s * 100),
    name: "saturation",
    label: "s" // We need to cast to any because @types/react-color does not define types correctly
    ,
    onChange: ({
      s
    }) => {
      props.onChange({ ...props.hsl,
        s: s / 100
      });
    }
  }))), jsx(Label, null, "Lightness", jsx(Input, _extends({}, props, {
    value: Math.round(props.hsl.l * 100),
    name: "lightness",
    label: "l" // We need to cast to any because @types/react-color does not define types correctly
    ,
    onChange: ({
      l
    }) => {
      props.onChange({ ...props.hsl,
        l: l / 100
      });
    }
  })))));
});
const ColorPicker = ({
  children,
  ...props
}) => {
  const popover = usePopoverState();
  const hasChildren = !!children;

  if (!hasChildren) {
    return jsx(Picker, props);
  }

  return jsx(React.Fragment, null, jsx(PopoverDisclosure, _extends({}, popover, {
    children: disclosure => jsx("div", disclosure, children)
  })), jsx(Popover, _extends({}, popover, {
    "aria-label": "Edit color",
    sx: {
      outline: 'none'
    }
  }), jsx(Picker, props)));
};

const Colors = props => {
  const context = useThemeUI();
  const {
    colorMode: mode,
    theme
  } = context;
  const colorsKey = theme.rawColors ? 'rawColors' : 'colors';
  const colors = theme[colorsKey];

  const onChange = key => val => {
    let next = {};

    if (mode && colors && colors.modes && colors.modes[mode] && // TODO: Remove any after @theme-ui/color-mode was transformed to TypeScript
    mode !== context.theme.initialColorMode) {
      next = {
        [colorsKey]: {
          modes: {
            [mode]: {
              [key]: val.hex
            }
          }
        }
      };
    } else {
      next = {
        [colorsKey]: {
          [key]: val.hex
        }
      };
    }

    context.setTheme(next);
  };

  return jsx(ColorPalette, _extends({}, props, {
    mode: mode,
    render: ({
      swatch,
      color,
      key
    }) => jsx(ColorPicker, {
      key: key,
      color: color,
      onChange: onChange(key)
    }, swatch)
  }));
};

/** @jsx jsx */
const defaultSpace = [0, 4, 8, 16, 32, 64, 128, 256, 512];

const Space = () => {
  const context = useThemeUI();
  const {
    space = defaultSpace
  } = context.theme || {};

  const onChange = key => e => {
    const n = parseFloat(e.target.value);

    if (Array.isArray(space)) {
      const i = parseInt(key);
      context.setTheme({
        space: [...space.slice(0, i), n, ...space.slice(i + 1)]
      });
    } else {
      context.setTheme({
        space: {
          [key]: n
        }
      });
    }
  };

  return jsx(Fragment, null, Object.keys(space).map(key => jsx("div", {
    key: key
  }, jsx(Field, {
    type: "number",
    label: key,
    name: 'space.' + key,
    value: space[key],
    onChange: onChange(key)
  }))));
};

const Chevron = () => jsx("svg", {
  viewBox: "0 0 16 16",
  width: "12",
  height: "12",
  sx: {
    pointerEvents: 'none'
  }
}, jsx("path", {
  stroke: "currentcolor",
  strokeWidth: "2",
  fill: "none",
  d: "M14 6 L8 12 L2 6"
}));

const keys = {
  38: 'up',
  40: 'down',
  27: 'escape',
  13: 'return'
};

const Combobox = ({
  type = 'text',
  name,
  label,
  value,
  onChange,
  options = [],
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(-1);
  const root = useRef(null);
  const input = useRef(null);
  useEffect(() => {
    const handleOutsideClick = e => {
      if (root.current && root.current.contains(e.target)) return;
      setOpen(false);
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [root.current]);
  useEffect(() => {
    resetIndex();
  }, [value]);
  const popup = name + '-popup';

  const resetIndex = () => {
    const i = options.indexOf(value);
    setIndex(i);
  };

  const handleKeyDown = e => {
    if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;

    switch (keys[e.keyCode]) {
      case 'up':
        if (!open) setOpen(true);
        if (index < 1) setIndex(options.length - 1);else setIndex(index - 1);
        break;

      case 'down':
        if (!open) setOpen(true);
        setIndex((index + 1) % options.length);
        break;

      case 'return':
        if (!open) return;
        const val = options[index];
        if (val) onChange(val);
        setOpen(false);
        break;

      case 'escape':
        setOpen(false);
        resetIndex();
        break;
    }
  };

  const handleChange = e => {
    onChange(e.target.value);
  };

  const handleBlur = () => {
    requestAnimationFrame(() => {
      if (root.current && root.current.contains(document.activeElement)) return;
      setOpen(false);
    });
  };

  const toggleOpen = () => {
    setOpen(!open);
    if (input.current) input.current.focus();
  };

  const handleItemClick = i => () => {
    const val = options[i];
    if (val) onChange(val);
    setOpen(false);
    input.current && input.current.select();
  };

  return jsx("div", {
    ref: root,
    style: {
      position: open ? 'relative' : 'static'
    },
    sx: {
      zIndex: 2
    }
  }, jsx(Label$1, {
    htmlFor: name
  }, label || name), jsx("div", {
    sx: {
      display: 'flex',
      alignItems: 'center'
    }
  }, jsx(Input$1, _extends({}, props, {
    ref: input,
    role: "combobox",
    type: type,
    id: name,
    name: name,
    value: value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    "aria-autocomplete": "none",
    "aria-haspopup": "true",
    "aria-owns": popup,
    "aria-expanded": open,
    "aria-activedescendant": name + index
  })), jsx("button", {
    tabIndex: -1,
    "aria-label": open ? 'Close' : 'Open',
    onClick: toggleOpen,
    sx: {
      appearance: 'none',
      width: 32,
      height: 32,
      color: 'inherit',
      bg: 'transparent',
      border: 0,
      ml: -32
    }
  }, jsx(Chevron, null))), jsx("ul", {
    id: popup,
    role: "listbox",
    "aria-label": name,
    tabIndex: -1,
    style: {
      visibility: open ? 'visible' : 'hidden',
      position: open ? 'absolute' : 'static'
    },
    sx: {
      left: 0,
      right: 0,
      top: '100%',
      listStyle: 'none',
      p: 0,
      m: 0,
      maxHeight: 512,
      overflowY: 'auto',
      bg: 'white',
      color: 'black',
      border: '1px solid',
      borderTop: 0,
      borderColor: 'lightgray'
    }
  }, open && options.map((option, i) => jsx("li", {
    key: option,
    role: "option",
    "aria-selected": i === index,
    onClick: handleItemClick(i),
    sx: {
      p: 1,
      userSelect: 'none',
      '&[aria-selected=true],:hover': {
        bg: 'highlight'
      }
    }
  }, option))));
};

/** @jsx jsx */
const defaultFonts = ['system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif', '"Helvetica Neue", Helvetica, Arial, sans-serif', '"Avenir Next", Helvetica, Arial, sans-serif', 'Verdana, sans-serif', 'Georgia, serif', 'Inter, sans-serif', 'Roboto, sans-serif', '"Roboto Condensed", sans-serif', '"Roboto Mono", monospace', '"Roboto Slab", serif', '"Open Sans", sans-serif', 'Lato, sans-serif', 'Montserrat, sans-serif', '"Source Sans Pro", sans-serif', '"Source Serif Pro", serif', '"Source Code Pro", monospace', '"Raleway", sans-serif', '"Oswald", sans-serif', '"Merriweather", serif', 'Menlo, monospace'];

const Fonts = ({
  options = defaultFonts
}) => {
  const context = useThemeUI();
  const {
    fonts = {}
  } = context.theme || {};

  const onChange = key => val => {
    context.setTheme({
      fonts: {
        [key]: val
      }
    });
  };

  return jsx(Fragment, null, Object.keys(fonts).map(key => jsx("div", {
    key: key
  }, jsx(Combobox, {
    label: key,
    name: 'fonts.' + key,
    value: fonts[key],
    onChange: onChange(key),
    options: options
  }))));
};

/** @jsx jsx */

const FontSizes = () => {
  const context = useThemeUI();
  const {
    fontSizes = []
  } = context.theme || {};

  const onChange = key => e => {
    const n = parseFloat(e.target.value);

    if (Array.isArray(fontSizes)) {
      const i = parseInt(key);
      context.setTheme({
        fontSizes: [...fontSizes.slice(0, i), n, ...fontSizes.slice(i + 1)]
      });
    } else {
      context.setTheme({
        fontSizes: {
          [key]: n
        }
      });
    }
  };

  return jsx(Fragment, null, Object.keys(fontSizes).map(key => jsx("div", {
    key: key
  }, jsx(Field, {
    type: "number",
    label: key,
    name: 'fontSizes.' + key,
    value: fontSizes[key],
    onChange: onChange(key)
  }))));
};

/** @jsx jsx */
const weights = [100, 200, 300, 400, 500, 600, 700, 800, 900];

const FontWeights = () => {
  const context = useThemeUI();
  const {
    fontWeights = {}
  } = context.theme || {};

  const onChange = key => val => {
    context.setTheme({
      fontWeights: {
        [key]: val
      }
    });
  };

  return jsx(Fragment, null, Object.keys(fontWeights).map(key => jsx("div", {
    key: key
  }, jsx(Combobox, {
    label: key,
    name: 'fontWeights.' + key,
    value: fontWeights[key],
    onChange: onChange(key),
    options: weights
  }))));
};

/** @jsx jsx */

const LineHeights = () => {
  const context = useThemeUI();
  const {
    lineHeights = {}
  } = context.theme || {};

  const onChange = key => e => {
    const n = parseFloat(e.target.value);
    context.setTheme({
      lineHeights: {
        [key]: n
      }
    });
  };

  return jsx(Fragment, null, Object.keys(lineHeights).map(key => jsx("div", {
    key: key
  }, jsx(Field, {
    type: "number",
    label: key,
    name: 'lineHeights.' + key,
    value: lineHeights[key],
    onChange: onChange(key),
    min: 1,
    max: 3,
    step: 1 / 64
  }))));
};

var index = {
  Colors,
  Space,
  Fonts,
  FontSizes,
  FontWeights,
  LineHeights
};

const ThemeColorPicker = ({
  children,
  theme,
  ...props
}) => {
  const popover = usePopoverState();
  const context = useThemeUI();

  const _theme = theme || context.theme || {};

  const colors = _theme.rawColors || _theme.colors || {}; // bug: only supports flat color scales

  const value = String(props.value && (colors[props.value] || props.value));
  const options = ['transparent', ...Object.keys(colors).map(key => colors[key]).filter(color => typeof color === 'string').filter(color => /^#/.test(color))];

  const onChange = color => {
    const [key] = Object.entries(colors).find(([k, v]) => tinycolor(v).toHexString() === color.hex) || [];
    props.onChange(key || color.hex || color);
  };

  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  });

  const onChangeComplete = () => {
    if (mounted.current) {
      popover.hide();
    }
  };

  return jsx(React.Fragment, null, jsx(PopoverDisclosure, _extends({}, popover, {
    children: disclosure => jsx("div", _extends({}, disclosure, {
      style: {
        backgroundColor: value
      },
      sx: {
        width: 32,
        height: 32,
        border: '1px solid',
        borderColor: 'lightgray'
      }
    }), children)
  })), jsx(Popover, _extends({}, popover, {
    "aria-label": "Choose Color",
    style: {
      zIndex: popover.visible ? 1 : undefined
    }
  }), jsx(GithubPicker, _extends({
    colors: options,
    triangle: "hide"
  }, props, {
    color: value,
    onChange: onChange,
    onChangeComplete: onChangeComplete
  }))));
};

/** @jsx jsx */
const Colors$1 = ({
  value: {
    color,
    bg
  } = {},
  theme,
  onChange
}) => {
  return jsx("div", {
    sx: {
      display: 'grid',
      gap: 2,
      gridTemplateColumns: 'repeat(2, 1fr)',
      my: 3
    }
  }, jsx("div", {
    sx: {
      display: 'flex',
      alignItems: 'center'
    }
  }, jsx(ThemeColorPicker, {
    theme: theme,
    value: color || '',
    onChange: color => {
      onChange({
        color: color
      });
    }
  }), jsx("div", {
    sx: {
      fontWeight: 'bold',
      ml: 2
    }
  }, "Color")), jsx("div", {
    sx: {
      display: 'flex',
      alignItems: 'center'
    }
  }, jsx(ThemeColorPicker, {
    theme: theme,
    value: bg || '',
    onChange: bg => {
      onChange({
        bg: bg
      });
    }
  }), jsx("div", {
    sx: {
      fontWeight: 'bold',
      ml: 2
    }
  }, "Background Color")));
};

/** @jsx jsx */
const Space$1 = ({
  tag,
  property = 'margin',
  value = {},
  onChange
}) => {
  const [lock, setLock] = useState({
    x: false,
    y: false
  });
  const key = property === 'margin' ? 'm' : 'p';
  const x = value[`${key}x`] || value[`${property}X`] || '';
  const y = value[`${key}y`] || value[`${property}Y`] || '';
  const t = lock.y ? y : value[`${key}t`] || value[`${property}Top`] || '';
  const b = lock.y ? y : value[`${key}b`] || value[`${property}Bottom`] || '';
  const l = lock.x ? x : value[`${key}l`] || value[`${property}Left`] || '';
  const r = lock.x ? x : value[`${key}r`] || value[`${property}Right`] || '';
  useEffect(() => {
    if (typeof x === 'number') setLock(lock => ({ ...lock,
      x: true
    }));
    if (typeof y === 'number') setLock(lock => ({ ...lock,
      y: true
    }));
  }, []);

  const handleChange = direction => e => {
    const n = e.target.value;
    const val = n === '' ? undefined : parseInt(n);

    switch (direction) {
      case 't':
        if (lock.y) onChange({
          [key + 'y']: val
        });else onChange({
          [key + 't']: val
        });
        break;

      case 'b':
        if (lock.y) onChange({
          [key + 'y']: val
        });else onChange({
          [key + 'b']: val
        });
        break;

      case 'l':
        if (lock.x) onChange({
          [key + 'x']: val
        });else onChange({
          [key + 'l']: val
        });
        break;

      case 'r':
        if (lock.x) onChange({
          [key + 'x']: val
        });else onChange({
          [key + 'r']: val
        });
        break;
    }
  };

  const onChangeLock = dir => e => {
    const isX = dir === 'x';

    if (!lock[dir]) {
      setLock(lock => ({ ...lock,
        [dir]: true
      }));
      const val = isX ? l || r : t || b;
      onChange({
        [key + (isX ? 'l' : 't')]: undefined,
        [key + (isX ? 'r' : 'b')]: undefined,
        [key + dir]: val
      });
    } else {
      setLock(lock => ({ ...lock,
        [dir]: false
      }));
      const val = dir === 'x' ? x : y;
      onChange({
        [key + (isX ? 'l' : 't')]: val,
        [key + (isX ? 'r' : 'b')]: val,
        [key + dir]: undefined
      });
    }
  };

  const prefixName = name => tag ? `styles.${tag}.${key}${name}` : key + name;

  const label = dir => property === 'margin' ? 'Margin ' + dir : 'Padding ' + dir;

  return jsx("div", {
    sx: {
      display: 'grid',
      gap: 2,
      gridTemplateColumns: 'repeat(3, 1fr)',
      alignItems: 'center'
    }
  }, jsx(Field, {
    type: "number",
    name: prefixName('l'),
    label: label('Left'),
    value: l,
    onChange: handleChange('l')
  }), jsx("div", {
    sx: {
      display: 'grid',
      gap: 2
    }
  }, jsx(Field, {
    type: "number",
    name: prefixName('t'),
    label: label('Top'),
    value: t,
    onChange: handleChange('t')
  }), jsx("div", null, jsx(Label$1, null, jsx(Checkbox, {
    checked: lock.x,
    onChange: onChangeLock('x')
  }), "Lock x-axis"), jsx(Label$1, null, jsx(Checkbox, {
    checked: lock.y,
    onChange: onChangeLock('y')
  }), "Lock y-axis")), jsx(Field, {
    type: "number",
    name: prefixName('b'),
    label: label('Bottom'),
    value: b,
    onChange: handleChange('b')
  })), jsx(Field, {
    type: "number",
    name: prefixName('r'),
    label: label('Right'),
    value: r,
    onChange: handleChange('r')
  }));
};

const Margin = props => jsx(Space$1, _extends({}, props, {
  property: "margin"
}));

const Padding = props => jsx(Space$1, _extends({}, props, {
  property: "padding"
}));

/** @jsx jsx */
const Typography = ({
  tag,
  value: {
    fontFamily,
    fontSize,
    fontWeight,
    lineHeight
  } = {},
  theme: {
    fonts = {},
    fontSizes = [],
    fontWeights = {},
    lineHeights = {}
  } = {},
  onChange
}) => {
  const prefixName = name => tag ? `styles.${tag}.${name}` : name;

  return jsx(Fragment, null, jsx(Combobox, {
    name: prefixName('fontFamily'),
    label: "Font Family",
    value: fontFamily || '',
    onChange: fontFamily => {
      onChange({
        fontFamily
      });
    },
    options: ['inherit', ...Object.keys(fonts)]
  }), jsx("div", {
    sx: {
      display: 'grid',
      gap: 2,
      gridTemplateColumns: 'repeat(3, 1fr)'
    }
  }, jsx(Field, {
    name: prefixName('fontSize'),
    label: "Font Size",
    value: fontSize || '',
    type: "number",
    onChange: e => {
      const fontSize = Number(e.target.value);
      onChange({
        fontSize
      });
    }
  }), jsx(Combobox, {
    name: prefixName('fontWeight'),
    label: "Font Weight",
    value: fontWeight || '',
    onChange: fontWeight => {
      onChange({
        fontWeight
      });
    },
    options: ['inherit', ...Object.keys(fontWeights)]
  }), jsx(Combobox, {
    name: prefixName('lineHeight'),
    label: "Line Height",
    value: lineHeight || '',
    onChange: lineHeight => {
      onChange({
        lineHeight
      });
    },
    options: ['inherit', ...Object.keys(lineHeights)]
  })));
};

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Colors: Colors$1,
  Margin: Margin,
  Padding: Padding,
  Typography: Typography
});

/** @jsx jsx */
function Styles({
  tag = 'root'
}) {
  const context = useThemeUI();
  const {
    styles = {}
  } = context.theme || {}; // todo: this is unsafe, and most probably a bug

  const style = styles[tag] || {};

  const setStyle = next => {
    context.setTheme({
      styles: {
        [tag]: { ...style,
          ...next
        }
      }
    });
  };

  return jsx(Fragment, null, jsx("b", null, "theme.styles.", tag), jsx(Typography, {
    value: style,
    onChange: setStyle,
    theme: context.theme || undefined
  }), jsx(Margin, {
    value: style,
    onChange: setStyle
  }), jsx(Colors$1, {
    value: style,
    onChange: setStyle
  }));
}

export { ColorPicker, EditorProvider, Styles, index$1 as Sx, index as Theme };
